import { PLANS } from "../../data/plans";

export enum PricingUnit
{
    None,
    User = "user",
    Project = "project",
}

export interface SubscriptionPlan
{
    id: string
    name: string,
    description: string,
    price: number,
    type: string,
    trial: number,
    paddleTrial: boolean,
    period: string,
    unit: PricingUnit,
    pricingDisplayName: string,
    pricingDescription: string,
    pricingIncludedFeatures: string[],
    pricingRequirements?: string,
}

const defaultIndieFeatures: string[] = [
    "Commercial license",
    "All features",
    "All versions",
    "1 user",
    "unlimited projects",
    "email support"
];

const defaultProFeatures: string[] = [
    "Commercial license",
    "All features",
    "All versions",
    "unlimited projects",
    "email support"
];

const defaultEnterpriseFeatures: string[] = [
    "Commercial license",
    "All features",
    "All versions",
    "Unlimited Users",
    "1 project",
    "priority support"
];

const freePricingFeatures: string[] = [
    "Commercial license",
    "All features",
    "All versions",
    "- Support",
    "Bug Reports",
    "",
    "- Platforms",
    "Windows",
    "Mac",
    "Linux"
];

const plusPricingFeatures: string[] = [
    "Commercial license",
    "All features",
    "All versions",
    "- Support",
    "Bug Reports",
    "Email Support",
    "- Platforms",
    "Windows",
    "Mac",
    "Linux",
    "Android",
    "iOS"
];

const proPricingFeatures: string[] = [
    "Commercial license",
    "All features",
    "All versions",
    "- Support",
    "Bug Reports",
    "Email Support",
    "- Platforms",
    "Windows",
    "Mac",
    "Linux",
    "Android",
    "iOS",
    "Xbox",
    "PlayStation",
    "Switch"
];

const defaultDescription = "Render vast amounts of vegetation on your terrain with this plugin.";

const defaultIndieRestrictions = "For individuals/companies with under $100k annual revenue or funding";
const defaultProRestrictions = "For companies with over $100k annual revenue or funding";
const defaultEnterpriseRestrictions = "For companies with over $100k annual revenue or funding";

const defaultIndieDescription = "Realize your vision";
const defaultProDescription = "Outperform the competition";

const defaultFreePricingDescription = "For Unity Personal users";
const defaultPlusPricingDescription = "For Unity Plus users";
const defaultProPricingDescription = "For Unity Pro users";

export const paddleSubscriptions: SubscriptionPlan[] = [

    // Staging new (Free, Plus, Pro)
    {
        id: PLANS.natureRenderer.free.staging,
        name: "Nature Renderer Free",
        description: defaultDescription,
        type: "Free Plan (Staging)",
        price: 0,
        period: "month",
        unit: PricingUnit.None,
        trial: 0,
        paddleTrial: false,
        pricingDisplayName: "Free",
        pricingDescription: defaultFreePricingDescription,
        pricingRequirements: "",
        pricingIncludedFeatures: freePricingFeatures
    },
    {
        id: PLANS.natureRenderer.plus.monthly.staging,
        name: "Nature Renderer Plus",
        description: defaultDescription,
        type: "Monthly Subscription (Staging)",
        price: 15,
        period: "month",
        unit: PricingUnit.User,
        trial: 0,
        paddleTrial: false,
        pricingDisplayName: "Plus",
        pricingDescription: defaultPlusPricingDescription,
        pricingRequirements: "",
        pricingIncludedFeatures: plusPricingFeatures
    },
    {
        id: PLANS.natureRenderer.plus.annual.staging,
        name: "Nature Renderer Plus",
        description: defaultDescription,
        type: "Annual Subscription (Staging)",
        price: 150,
        period: "year",
        unit: PricingUnit.User,
        trial: 0,
        paddleTrial: false,
        pricingDisplayName: "Plus",
        pricingDescription: defaultPlusPricingDescription,
        pricingRequirements: "",
        pricingIncludedFeatures: plusPricingFeatures
    },
    {
        id: PLANS.natureRenderer.pro.monthly.staging,
        name: "Nature Renderer Pro",
        description: defaultDescription,
        type: "Monthly Subscription (Staging)",
        price: 30,
        period: "month",
        unit: PricingUnit.User,
        trial: 0,
        paddleTrial: false,
        pricingDisplayName: "Pro",
        pricingDescription: defaultProPricingDescription,
        pricingRequirements: "",
        pricingIncludedFeatures: proPricingFeatures
    },
    {
        id: PLANS.natureRenderer.pro.annual.staging,
        name: "Nature Renderer Pro",
        description: defaultDescription,
        type: "Annual Subscription (Staging)",
        price: 300,
        period: "year",
        unit: PricingUnit.User,
        trial: 0,
        paddleTrial: false,
        pricingDisplayName: "Pro",
        pricingDescription: defaultProPricingDescription,
        pricingRequirements: "",
        pricingIncludedFeatures: proPricingFeatures
    },

    // Production new (Free, Plus, Pro)
    {
        id: PLANS.natureRenderer.free.production,
        name: "Nature Renderer Free",
        description: defaultDescription,
        type: "Free Plan",
        price: 0,
        period: "month",
        unit: PricingUnit.None,
        trial: 0,
        paddleTrial: false,
        pricingDisplayName: "Free",
        pricingDescription: defaultFreePricingDescription,
        pricingRequirements: "",
        pricingIncludedFeatures: freePricingFeatures
    },
    {
        id: PLANS.natureRenderer.plus.monthly.production,
        name: "Nature Renderer Plus",
        description: defaultDescription,
        type: "Monthly Subscription",
        price: 15,
        period: "month",
        unit: PricingUnit.User,
        trial: 0,
        paddleTrial: false,
        pricingDisplayName: "Plus",
        pricingDescription: defaultPlusPricingDescription,
        pricingRequirements: "",
        pricingIncludedFeatures: plusPricingFeatures
    },
    {
        id: PLANS.natureRenderer.plus.annual.production,
        name: "Nature Renderer Plus",
        description: defaultDescription,
        type: "Annual Subscription",
        price: 150,
        period: "year",
        unit: PricingUnit.User,
        trial: 0,
        paddleTrial: false,
        pricingDisplayName: "Plus",
        pricingDescription: defaultPlusPricingDescription,
        pricingRequirements: "",
        pricingIncludedFeatures: plusPricingFeatures
    },
    {
        id: PLANS.natureRenderer.pro.monthly.production,
        name: "Nature Renderer Pro",
        description: defaultDescription,
        type: "Monthly Subscription",
        price: 30,
        period: "month",
        unit: PricingUnit.User,
        trial: 0,
        paddleTrial: false,
        pricingDisplayName: "Pro",
        pricingDescription: defaultProPricingDescription,
        pricingRequirements: "",
        pricingIncludedFeatures: proPricingFeatures
    },
    {
        id: PLANS.natureRenderer.pro.annual.production,
        name: "Nature Renderer Pro",
        description: defaultDescription,
        type: "Annual Subscription",
        price: 300,
        period: "year",
        unit: PricingUnit.User,
        trial: 0,
        paddleTrial: false,
        pricingDisplayName: "Pro",
        pricingDescription: defaultProPricingDescription,
        pricingRequirements: "",
        pricingIncludedFeatures: proPricingFeatures
    },

    // Staging
    {
        id: "803805",
        name: "Nature Renderer Pro",
        description: defaultDescription,
        type: "Monthly Subscription (Staging)",
        price: 15,
        period: "month",
        unit: PricingUnit.User,
        trial: 0,
        paddleTrial: false,
        pricingDisplayName: "Pro",
        pricingDescription: defaultProDescription,
        pricingRequirements: defaultProRestrictions,
        pricingIncludedFeatures: defaultProFeatures
    },

    {
        id: "803804",
        name: "Nature Renderer Indie",
        description: defaultDescription,
        type: "Monthly Subscription (Staging)",
        price: 6,
        period: "month",
        unit: PricingUnit.User,
        trial: 0,
        paddleTrial: false,
        pricingDisplayName: "Indie",
        pricingDescription: defaultIndieDescription,
        pricingRequirements: defaultIndieRestrictions,
        pricingIncludedFeatures: defaultIndieFeatures
    },

    {
        id: "803801",
        name: "Nature Renderer Pro",
        description: defaultDescription,
        type: "Annual Subscription (Staging)",
        price: 150,
        period: "year",
        unit: PricingUnit.User,
        trial: 0,
        paddleTrial: false,
        pricingDisplayName: "Pro",
        pricingDescription: defaultProDescription,
        pricingRequirements: defaultProRestrictions,
        pricingIncludedFeatures: defaultProFeatures
    },

    {
        id: "803803",
        name: "Nature Renderer Enterprise",
        description: defaultDescription,
        type: "Annual Subscription (Staging)",
        price: 9000,
        period: "year",
        unit: PricingUnit.Project,
        trial: 0,
        paddleTrial: false,
        pricingDisplayName: "Enterprise",
        pricingDescription: "Build successful large projects",
        pricingRequirements: defaultEnterpriseRestrictions,
        pricingIncludedFeatures: defaultEnterpriseFeatures
    },

    {
        id: "803802",
        name: "Nature Renderer Indie",
        description: defaultDescription,
        type: "Annual Subscription (Staging)",
        price: 60,
        period: "year",
        unit: PricingUnit.User,
        trial: 0,
        paddleTrial: false,
        pricingDisplayName: "Indie",
        pricingDescription: defaultIndieDescription,
        pricingRequirements: defaultIndieRestrictions,
        pricingIncludedFeatures: defaultIndieFeatures
    },

    {
        id: "798039",
        name: "Nature Renderer",
        description: defaultDescription,
        type: "Annual Subscription (Staging)",
        price: 60,
        period: "year",
        unit: PricingUnit.User,
        trial: 0,
        paddleTrial: false,
        pricingDisplayName: "Indie",
        pricingDescription: "Achieve your vision",
        pricingIncludedFeatures: defaultIndieFeatures
    },
    {
        id: "787859",
        name: "Nature Renderer",
        description: defaultDescription,
        type: "Monthly Subscription (Staging)",
        price: 6,
        period: "month",
        unit: PricingUnit.User,
        trial: 14,
        paddleTrial: true,
        pricingDisplayName: "Indie",
        pricingDescription: "Achieve your vision",
        pricingIncludedFeatures: defaultIndieFeatures
    },
    {
        id: "787161",
        name: "Nature Renderer Upgrade",
        description: defaultDescription,
        type: "Monthly Subscription (Staging)",
        price: 6,
        period: "month",
        unit: PricingUnit.User,
        trial: 90,
        paddleTrial: true,
        pricingDisplayName: "Indie",
        pricingDescription: "Achieve your vision",
        pricingIncludedFeatures: defaultIndieFeatures
    },
    {
        id: "752903",
        name: "Nature Renderer",
        description: defaultDescription,
        type: "Monthly Subscription (Staging)", // No trial
        price: 6,
        period: "month",
        unit: PricingUnit.User,
        trial: 14, // Display only
        paddleTrial: false,
        pricingDisplayName: "Indie",
        pricingDescription: "Achieve your vision",
        pricingIncludedFeatures: defaultIndieFeatures
    },
    {
        id: "787861",
        name: "Nature Renderer",
        description: defaultDescription,
        type: "Monthly Subscription (Staging)", // No trial
        price: 6,
        period: "month",
        unit: PricingUnit.User,
        trial: 90, // Display only
        paddleTrial: false,
        pricingDisplayName: "Indie",
        pricingDescription: "Achieve your vision",
        pricingIncludedFeatures: defaultIndieFeatures
    },
    {
        id: "793386",
        name: "Nature Renderer",
        description: defaultDescription,
        type: "Monthly Subscription (Staging)", // No trial
        price: 6,
        period: "month",
        unit: PricingUnit.User,
        trial: 0, // Display only
        paddleTrial: false,
        pricingDisplayName: "Indie",
        pricingDescription: "Achieve your vision",
        pricingIncludedFeatures: defaultIndieFeatures
    },

    // Production New (Inde, Pro, Enterprise)
    {
        id: "804753",
        name: "Nature Renderer Indie",
        description: defaultDescription,
        type: "Monthly Subscription",
        price: 6,
        period: "month",
        unit: PricingUnit.User,
        trial: 0,
        paddleTrial: false,
        pricingDisplayName: "Indie",
        pricingDescription: defaultIndieDescription,
        pricingRequirements: defaultIndieRestrictions,
        pricingIncludedFeatures: defaultIndieFeatures
    },
    {
        id: "804755",
        name: "Nature Renderer Pro",
        description: defaultDescription,
        type: "Monthly Subscription",
        price: 15,
        period: "month",
        unit: PricingUnit.User,
        trial: 0,
        paddleTrial: false,
        pricingDisplayName: "Pro",
        pricingDescription: defaultProDescription,
        pricingRequirements: defaultProRestrictions,
        pricingIncludedFeatures: defaultProFeatures
    },
    {
        id: "804754",
        name: "Nature Renderer Indie",
        description: defaultDescription,
        type: "Annual Subscription",
        price: 60,
        period: "year",
        unit: PricingUnit.User,
        trial: 0,
        paddleTrial: false,
        pricingDisplayName: "Indie",
        pricingDescription: defaultIndieDescription,
        pricingRequirements: defaultIndieRestrictions,
        pricingIncludedFeatures: defaultIndieFeatures
    },
    {
        id: "804756",
        name: "Nature Renderer Pro",
        description: defaultDescription,
        type: "Annual Subscription",
        price: 150,
        period: "year",
        unit: PricingUnit.User,
        trial: 0,
        paddleTrial: false,
        pricingDisplayName: "Pro",
        pricingDescription: defaultProDescription,
        pricingRequirements: defaultProRestrictions,
        pricingIncludedFeatures: defaultProFeatures
    },

    // Production
    {
        id: "798040",
        name: "Nature Renderer",
        description: defaultDescription,
        type: "Annual Subscription",
        price: 60,
        period: "year",
        unit: PricingUnit.User,
        trial: 0,
        paddleTrial: false,
        pricingDisplayName: "Indie",
        pricingDescription: "Achieve your vision",
        pricingIncludedFeatures: defaultIndieFeatures
    },
    {
        id: "742917",
        name: "Nature Renderer",
        description: defaultDescription,
        type: "Monthly Subscription", // 14-day trial
        price: 8,
        period: "month",
        unit: PricingUnit.User,
        trial: 14,
        paddleTrial: true,
        pricingDisplayName: "Indie",
        pricingDescription: "Achieve your vision",
        pricingIncludedFeatures: defaultIndieFeatures
    },
    {
        id: "760890",
        name: "Nature Renderer",
        description: defaultDescription,
        type: "Monthly Subscription", // 90-day trial
        price: 8,
        period: "month",
        unit: PricingUnit.User,
        trial: 90,
        paddleTrial: true,
        pricingDisplayName: "Indie",
        pricingDescription: "Achieve your vision",
        pricingIncludedFeatures: defaultIndieFeatures
    },
    {
        id: "770898",
        name: "Nature Renderer",
        description: defaultDescription,
        type: "Monthly Subscription", // 14-day trial
        price: 6,
        period: "month",
        unit: PricingUnit.User,
        trial: 14,
        paddleTrial: true,
        pricingDisplayName: "Indie",
        pricingDescription: "Achieve your vision",
        pricingIncludedFeatures: defaultIndieFeatures
    },
    {
        id: "770899",
        name: "Nature Renderer",
        description: defaultDescription,
        type: "Monthly Subscription", // 90-day trial
        price: 6,
        period: "month",
        unit: PricingUnit.User,
        trial: 90,
        paddleTrial: true,
        pricingDisplayName: "Indie",
        pricingDescription: "Achieve your vision",
        pricingIncludedFeatures: defaultIndieFeatures
    },
    {
        id: "787869",
        name: "Nature Renderer",
        description: defaultDescription,
        type: "Monthly Subscription",
        price: 6,
        period: "month",
        unit: PricingUnit.User,
        trial: 14, // 14-day custom trial. 0-day Paddle trial
        paddleTrial: false,
        pricingDisplayName: "Indie",
        pricingDescription: "Achieve your vision",
        pricingIncludedFeatures: defaultIndieFeatures
    },
    {
        id: "787870",
        name: "Nature Renderer",
        description: defaultDescription,
        type: "Monthly Subscription",
        price: 6,
        period: "month",
        unit: PricingUnit.User,
        trial: 90, // 90-day custom trial. 0-day Paddle trial
        paddleTrial: false,
        pricingDisplayName: "Indie",
        pricingDescription: "Achieve your vision",
        pricingIncludedFeatures: defaultIndieFeatures
    },
    {
        id: "793387",
        name: "Nature Renderer",
        description: defaultDescription,
        type: "Monthly Subscription",
        price: 6,
        period: "month",
        unit: PricingUnit.User,
        trial: 0, // No trial
        paddleTrial: false,
        pricingDisplayName: "Indie",
        pricingDescription: "Achieve your vision",
        pricingIncludedFeatures: defaultIndieFeatures
    }
];

export function getSubscriptionPlan (id: string): SubscriptionPlan | undefined
{
    return paddleSubscriptions.find(s => s.id == id) as SubscriptionPlan;
}
